import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import {
    ArrowLeft,
    Clock,
    ForkKnife,
    Receipt,
    FileText,
    Buildings,
} from "@phosphor-icons/react";

const eur = (n) =>
    new Intl.NumberFormat("es-ES", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 2,
    }).format(Number(n) || 0);

const hrs = (n) =>
    (Number(n) || 0).toLocaleString("es-ES", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

const CAT_LABELS = {
    dieta: "Dieta / manutención",
    desplazamiento: "Desplazamiento",
    alojamiento: "Alojamiento",
    material: "Material",
    otro: "Otro",
};

function StatCard({ label, value, hint, Icon, testid }) {
    return (
        <div className="bg-white p-5" data-testid={testid}>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-slate-500">
                {Icon && <Icon size={13} />} {label}
            </div>
            <div className="font-mono text-2xl font-bold mt-2 text-slate-950">{value}</div>
            {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
        </div>
    );
}

function Section({ title, count, children, testid }) {
    return (
        <div className="border border-slate-200 bg-white" data-testid={testid}>
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                <div className="font-display text-lg font-semibold">{title}</div>
                {count != null && (
                    <span className="text-xs text-slate-500 font-mono">
                        {count} {count === 1 ? "registro" : "registros"}
                    </span>
                )}
            </div>
            <div className="overflow-x-auto">{children}</div>
        </div>
    );
}

const Th = ({ children, right }) => (
    <th
        className={`py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold ${
            right ? "text-right" : "text-left"
        }`}
    >
        {children}
    </th>
);

const Empty = ({ cols, children }) => (
    <tr>
        <td colSpan={cols} className="text-center py-10 text-slate-400 text-sm">
            {children}
        </td>
    </tr>
);

export default function AdminProjectDetail() {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        setBusy(true);
        http.get(`/projects/${projectId}/detail`)
            .then((r) => setData(r.data))
            .catch((e) =>
                setError(e?.response?.data?.detail || "No se pudo cargar la ficha"),
            )
            .finally(() => setBusy(false));
    }, [projectId]);

    if (busy) {
        return (
            <div className="p-10 text-slate-500" data-testid="project-detail-loading">
                Cargando ficha de proyecto…
            </div>
        );
    }
    if (error) {
        return (
            <div className="p-10" data-testid="project-detail-error">
                <div className="text-red-600 mb-4">{error}</div>
                <Button variant="outline" onClick={() => navigate("/admin/projects")}>
                    <ArrowLeft size={14} className="mr-2" /> Volver a Proyectos
                </Button>
            </div>
        );
    }

    const p = data.project;

    return (
        <div data-testid="page-project-detail">
            <PageHeader
                eyebrow={
                    [p.customer_code, p.customer_name].filter(Boolean).join(" · ") ||
                    "Proyecto"
                }
                title={p.name}
                description={p.project_code ? `Código ${p.project_code}` : "Ficha de proyecto"}
                actions={
                    <Button
                        variant="outline"
                        onClick={() => navigate("/admin/projects")}
                        className="rounded-sm border-slate-300"
                        data-testid="project-detail-back"
                    >
                        <ArrowLeft size={14} className="mr-2" /> Volver
                    </Button>
                }
            />

            <div className="p-6 lg:p-10 space-y-6">
                {/* Datos del proyecto */}
                <div className="border border-slate-200 bg-white p-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                            Cliente
                        </div>
                        <div className="text-sm font-medium mt-1 text-slate-900">
                            {p.customer_name || "—"}
                        </div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                            Manager
                        </div>
                        <div className="text-sm font-medium mt-1 text-slate-900">
                            {p.manager_name || "—"}
                        </div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                            Horas planificadas
                        </div>
                        <div className="text-sm font-mono font-medium mt-1 text-slate-900">
                            {hrs(p.planned_hours)} h
                        </div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                            Presupuesto
                        </div>
                        <div className="text-sm font-mono font-medium mt-1 text-slate-900">
                            {p.planned_budget ? eur(p.planned_budget) : "—"}
                        </div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                            Estado
                        </div>
                        <div className="text-sm font-medium mt-1">
                            {!p.active ? (
                                <span className="text-slate-500">Archivado</span>
                            ) : (
                                <span className="text-emerald-700">Activo</span>
                            )}
                        </div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                            Facturable
                        </div>
                        <div className="text-sm font-medium mt-1 text-slate-900">
                            {p.billable ? "Sí" : "No"}
                        </div>
                    </div>
                </div>

                {/* KPIs */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
                    <StatCard
                        label="Horas imputadas"
                        value={`${hrs(data.hours.total_hours)} h`}
                        hint={`Coste estimado ${eur(data.hours.total_cost)}`}
                        Icon={Clock}
                        testid="kpi-hours"
                    />
                    <StatCard
                        label="Dietas / gastos"
                        value={eur(data.expenses.total)}
                        hint={`${data.expenses.count} registros`}
                        Icon={ForkKnife}
                        testid="kpi-expenses"
                    />
                    <StatCard
                        label="Facturas emitidas"
                        value={eur(data.invoices.total)}
                        hint={`${data.invoices.count} facturas`}
                        Icon={Receipt}
                        testid="kpi-invoices"
                    />
                    <StatCard
                        label="Facturas soportadas"
                        value={eur(data.supplier_invoices.total)}
                        hint={`${data.supplier_invoices.count} facturas`}
                        Icon={Buildings}
                        testid="kpi-supplier"
                    />
                </div>

                {/* Horas por persona */}
                <Section
                    title="Horas imputadas por persona"
                    count={data.hours.by_person.length}
                    testid="section-hours"
                >
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 border-b border-slate-200">
                            <tr>
                                <Th>Empleado</Th>
                                <Th right>Tarifa</Th>
                                <Th right>Horas</Th>
                                <Th right>Coste</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.hours.by_person.length === 0 && (
                                <Empty cols={4}>Sin horas imputadas.</Empty>
                            )}
                            {data.hours.by_person.map((r) => (
                                <tr
                                    key={r.user_id}
                                    className="border-b border-slate-100 hover:bg-slate-50"
                                >
                                    <td className="py-2.5 px-4 text-slate-800 font-medium">
                                        {r.user_name}
                                    </td>
                                    <td className="py-2.5 px-4 text-right font-mono text-slate-600">
                                        {eur(r.hourly_rate)}
                                    </td>
                                    <td className="py-2.5 px-4 text-right font-mono font-semibold">
                                        {hrs(r.hours)}
                                    </td>
                                    <td className="py-2.5 px-4 text-right font-mono font-semibold">
                                        {eur(r.cost)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Section>

                {/* Dietas / gastos */}
                <Section
                    title="Dietas y gastos asociados"
                    count={data.expenses.count}
                    testid="section-expenses"
                >
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 border-b border-slate-200">
                            <tr>
                                <Th>Fecha</Th>
                                <Th>Empleado</Th>
                                <Th>Categoría</Th>
                                <Th>Descripción</Th>
                                <Th right>Importe</Th>
                                <Th>Estado</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.expenses.count === 0 && (
                                <Empty cols={6}>Sin dietas ni gastos.</Empty>
                            )}
                            {data.expenses.rows.map((e) => (
                                <tr
                                    key={e.expense_id}
                                    className="border-b border-slate-100 hover:bg-slate-50"
                                >
                                    <td className="py-2.5 px-4 font-mono text-slate-700">
                                        {e.expense_date}
                                    </td>
                                    <td className="py-2.5 px-4 text-slate-800">
                                        {e.user_name}
                                    </td>
                                    <td className="py-2.5 px-4 text-slate-700">
                                        {CAT_LABELS[e.category] || e.category}
                                    </td>
                                    <td className="py-2.5 px-4 text-slate-600 max-w-xs truncate">
                                        {e.description || "—"}
                                    </td>
                                    <td className="py-2.5 px-4 text-right font-mono font-semibold">
                                        {eur(e.amount)}
                                    </td>
                                    <td className="py-2.5 px-4">
                                        <span
                                            className={`px-2 py-0.5 rounded-sm text-[10px] font-semibold uppercase ${
                                                e.paid
                                                    ? "bg-emerald-50 text-emerald-700"
                                                    : "bg-amber-50 text-amber-700"
                                            }`}
                                        >
                                            {e.paid ? "Pagado" : "Pendiente"}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Section>

                {/* Facturas emitidas */}
                <Section
                    title="Facturas emitidas"
                    count={data.invoices.count}
                    testid="section-invoices"
                >
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 border-b border-slate-200">
                            <tr>
                                <Th>Fecha</Th>
                                <Th>Nº</Th>
                                <Th>Concepto</Th>
                                <Th>Empresa</Th>
                                <Th right>Importe</Th>
                                <Th>Estado</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.invoices.count === 0 && (
                                <Empty cols={6}>Sin facturas emitidas.</Empty>
                            )}
                            {data.invoices.rows.map((inv) => (
                                <tr
                                    key={inv.invoice_id}
                                    className="border-b border-slate-100 hover:bg-slate-50"
                                >
                                    <td className="py-2.5 px-4 font-mono text-slate-700">
                                        {inv.invoice_date}
                                    </td>
                                    <td className="py-2.5 px-4 font-mono text-slate-600">
                                        {inv.invoice_number || "—"}
                                    </td>
                                    <td className="py-2.5 px-4 text-slate-800 max-w-xs truncate">
                                        {inv.title}
                                    </td>
                                    <td className="py-2.5 px-4 text-slate-700">
                                        {inv.company || "—"}
                                    </td>
                                    <td className="py-2.5 px-4 text-right font-mono font-semibold">
                                        {eur(inv.amount)}
                                    </td>
                                    <td className="py-2.5 px-4 text-slate-600 text-xs">
                                        {inv.status}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Section>

                {/* Facturas soportadas */}
                <Section
                    title="Facturas soportadas"
                    count={data.supplier_invoices.count}
                    testid="section-supplier"
                >
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 border-b border-slate-200">
                            <tr>
                                <Th>Fecha</Th>
                                <Th>Nº</Th>
                                <Th>Proveedor</Th>
                                <Th>Concepto</Th>
                                <Th right>Importe</Th>
                                <Th>Estado</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.supplier_invoices.count === 0 && (
                                <Empty cols={6}>Sin facturas soportadas.</Empty>
                            )}
                            {data.supplier_invoices.rows.map((inv) => (
                                <tr
                                    key={inv.invoice_id}
                                    className="border-b border-slate-100 hover:bg-slate-50"
                                >
                                    <td className="py-2.5 px-4 font-mono text-slate-700">
                                        {inv.date || "—"}
                                    </td>
                                    <td className="py-2.5 px-4 font-mono text-slate-600">
                                        {inv.invoice_number || "—"}
                                    </td>
                                    <td className="py-2.5 px-4 text-slate-800">
                                        {inv.supplier}
                                    </td>
                                    <td className="py-2.5 px-4 text-slate-600 max-w-xs truncate">
                                        {inv.concept || "—"}
                                    </td>
                                    <td className="py-2.5 px-4 text-right font-mono font-semibold">
                                        {eur(inv.total_amount)}
                                    </td>
                                    <td className="py-2.5 px-4">
                                        <span
                                            className={`px-2 py-0.5 rounded-sm text-[10px] font-semibold uppercase ${
                                                inv.paid
                                                    ? "bg-emerald-50 text-emerald-700"
                                                    : "bg-amber-50 text-amber-700"
                                            }`}
                                        >
                                            {inv.paid ? "Pagada" : "Pendiente"}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Section>
            </div>
        </div>
    );
}
